import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'
import type {
  ApiToken,
  ApiTokenPayload,
  CreatedApiToken,
  PluginConfig,
  PluginConfigPayload,
  PluginKey,
  PluginKeyPayload,
} from '../../features/account/apiAccess/types'

/** The account's active plugin keys, newest first. */
export const fetchPluginKeys = async (
  accountUid: string
): Promise<PluginKey[]> => {
  const response = await req(ENDPOINTS.ACCOUNT.PLUGIN_KEYS(accountUid))
  return (response?.data ?? []) as PluginKey[]
}

export const createPluginKey = async (
  accountUid: string,
  payload: PluginKeyPayload
): Promise<PluginKey> => {
  const response = await req(ENDPOINTS.ACCOUNT.PLUGIN_KEYS(accountUid), {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return response?.data as PluginKey
}

/** Name, websites and site-builder setting. The key and environment never change. */
export const updatePluginKey = async (
  accountUid: string,
  pluginKeyId: string,
  payload: PluginKeyPayload
): Promise<PluginKey> => {
  const response = await req(
    ENDPOINTS.ACCOUNT.PLUGIN_KEY(accountUid, pluginKeyId),
    { method: 'PUT', body: JSON.stringify(payload) }
  )
  return response?.data as PluginKey
}

/** Stops the key working on every website. Kept server-side for support. */
export const revokePluginKey = async (
  accountUid: string,
  pluginKeyId: string
): Promise<void> => {
  await req(ENDPOINTS.ACCOUNT.PLUGIN_KEY(accountUid, pluginKeyId), {
    method: 'DELETE',
  })
}

/** The signed-in user's API tokens. Secrets are never listed. */
export const fetchApiTokens = async (
  accountUid: string
): Promise<ApiToken[]> => {
  const response = await req(ENDPOINTS.ACCOUNT.API_TOKENS(accountUid))
  return (response?.data ?? []) as ApiToken[]
}

/** Creates a token; the response is the only time its secret is available. */
export const createApiToken = async (
  accountUid: string,
  payload: ApiTokenPayload
): Promise<CreatedApiToken> => {
  const response = await req(ENDPOINTS.ACCOUNT.API_TOKENS(accountUid), {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return {
    apiToken: response?.data as ApiToken,
    plainTextToken: response?.token as string,
  }
}

export const deleteApiToken = async (
  accountUid: string,
  apiTokenId: number
): Promise<void> => {
  await req(ENDPOINTS.ACCOUNT.API_TOKEN(accountUid, apiTokenId), {
    method: 'DELETE',
  })
}

/** The account's Website Plugin settings, or the plugin's defaults. */
export const fetchPluginConfig = async (
  accountUid: string
): Promise<PluginConfig> => {
  const response = await req(ENDPOINTS.ACCOUNT.PLUGIN_CONFIG(accountUid))
  return response?.data as PluginConfig
}

/** Saves every tab switch and colour at once. */
export const updatePluginConfig = async (
  accountUid: string,
  payload: PluginConfigPayload
): Promise<PluginConfig> => {
  const response = await req(ENDPOINTS.ACCOUNT.PLUGIN_CONFIG(accountUid), {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  return response?.data as PluginConfig
}
